import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { liveApi, buildLiveWsUrl, getPlayerTempId } from "@/lib/liveApi";
import { toast } from "sonner";
import { Users, Lightning, SignIn, CheckCircle, XCircle } from "@phosphor-icons/react";

const AVATARS = ["🎮", "🚀", "🎯", "🎲", "🎨", "🎭", "🎪", "🏆", "🔥", "⚡", "🌟", "💎"];
const NAME_KEY_PREFIX = "nk_live_player_name_";
const AVATAR_KEY_PREFIX = "nk_live_player_avatar_";

export default function PlayerView() {
  const { code } = useParams();
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [me, setMe] = useState(null);
  const [currentQ, setCurrentQ] = useState(null);
  const [myPick, setMyPick] = useState(null);
  const [myText, setMyText] = useState("");
  const [lastResult, setLastResult] = useState(null);   // {qid, is_correct, points}
  const [error, setError] = useState("");
  const wsRef = useRef(null);

  useEffect(() => {
    let closed = false;
    let retryTimer = null;
    let retryDelay = 1000;

    const hydrate = () => liveApi.get(`/live/events/${code}`).then((r) => {
      setEvent(r.data.event);
      setPlayers(r.data.players || []);
      setCurrentQ(r.data.current_question || null);
      const tempid = getPlayerTempId();
      const found = (r.data.players || []).find((p) => p.player_tempid === tempid);
      if (found) setMe(found);
    }).catch((e) => setError(e.response?.data?.detail || "Event not found"));

    const connect = () => {
      const ws = new WebSocket(buildLiveWsUrl({
        code, role: "player", playerTempId: getPlayerTempId(),
      }));
      wsRef.current = ws;
      ws.onopen = () => { retryDelay = 1000; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "snapshot") {
            setEvent(msg.data.event); setPlayers(msg.data.players || []);
            setCurrentQ(msg.data.current_question || null);
            const found = (msg.data.players || []).find((p) => p.player_tempid === getPlayerTempId());
            if (found) setMe(found);
          } else if (msg.type === "lobby_updated") {
            setPlayers(msg.data.players || []);
            const found = (msg.data.players || []).find((p) => p.player_tempid === getPlayerTempId());
            if (found) setMe(found);
          } else if (msg.type === "state_changed" || msg.type === "event_config_updated") {
            setEvent(msg.data);
          } else if (msg.type === "question_started") {
            setCurrentQ(msg.data); setMyPick(null); setMyText(""); setLastResult(null);
          } else if (msg.type === "question_ended") {
            setCurrentQ((prev) => prev ? {
              ...prev, revealed: true,
              answer_index: msg.data.correct_index,
              accepted_answer: msg.data.correct_answer,
            } : prev);
            setPlayers(msg.data.scoreboard || []);
            const tempid = getPlayerTempId();
            const meRow = (msg.data.scoreboard || []).find((p) => p.player_tempid === tempid);
            if (meRow) setMe(meRow);
            // Correctness — depends on question type
            const qtype = msg.data.type || "multiple_choice";
            if (qtype === "multiple_choice" && myPickRef.current != null) {
              setLastResult({ is_correct: myPickRef.current === msg.data.correct_index });
            } else if (qtype !== "multiple_choice" && myTextRef.current) {
              // Backend already normalized; do a light client-side check for immediate feedback
              const norm = (s) => (s || "").toLowerCase().replace(/[\s'’.,!?;:-]/g, "");
              const isCorrect = norm(myTextRef.current) === norm(msg.data.correct_answer || "");
              setLastResult({ is_correct: isCorrect });
            }
          } else if (msg.type === "scoreboard_updated") {
            setPlayers(msg.data.scoreboard || []);
            const tempid = getPlayerTempId();
            const meRow = (msg.data.scoreboard || []).find((p) => p.player_tempid === tempid);
            if (meRow) setMe(meRow);
          } else if (msg.type === "ladder_contestant_changed") {
            setEvent((prev) => prev ? { ...prev, ladder_active_contestant: msg.data.player_tempid } : prev);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (closed) return;
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, 10000);
          hydrate(); connect();
        }, retryDelay);
      };
    };
    hydrate(); connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [code]);

  // Keep a ref of myPick + myText so the ws handler can read it without triggering a re-effect
  const myPickRef = useRef(null);
  const myTextRef = useRef("");
  useEffect(() => { myPickRef.current = myPick; }, [myPick]);
  useEffect(() => { myTextRef.current = myText; }, [myText]);

  if (error) return (
    <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center px-6">
      <p className="text-sm text-nk-bad" data-testid="player-error">{error}</p>
    </div>
  );
  if (!event) return <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center"><p className="text-sm text-nk-mute">Loading…</p></div>;

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text px-5 pt-8 pb-16" data-testid="player-view">
      <header className="mb-6 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-nk-gold/70">NKognito Live</p>
        <h1 className="font-display text-2xl mt-2">{event.title}</h1>
        <p className="text-xs text-nk-mute mt-1">
          {event.mode.toUpperCase()} · {event.template} · Code <b className="text-nk-gold">{event.code}</b>
        </p>
      </header>

      {!me ? (
        <NameForm code={event.code} onJoined={(p) => setMe(p)} eventState={event.state} />
      ) : event.state === "ended" ? (
        <EndedCard me={me} players={players} />
      ) : currentQ && event.state === "running" ? (
        <QuestionCard
          code={event.code}
          q={currentQ}
          myPick={myPick}
          setMyPick={setMyPick}
          myText={myText}
          setMyText={setMyText}
          lastResult={lastResult}
          me={me}
          event={event}
        />
      ) : (
        <PlayerLobby me={me} players={players} eventState={event.state} event={event} />
      )}
    </div>
  );
}


function NameForm({ code, onJoined, eventState }) {
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY_PREFIX + code) || "");
  const [avatar, setAvatar] = useState(() => localStorage.getItem(AVATAR_KEY_PREFIX + code) || AVATARS[0]);
  const [busy, setBusy] = useState(false);

  const canJoin = name.trim().length >= 1 && name.trim().length <= 24;
  const notOpen = eventState !== "lobby" && eventState !== "running";

  const submit = async (e) => {
    e.preventDefault();
    if (!canJoin || notOpen) return;
    setBusy(true);
    try {
      const { data } = await liveApi.post(`/live/events/${code}/join`, {
        player_tempid: getPlayerTempId(),
        display_name: name.trim(),
        avatar,
      });
      localStorage.setItem(NAME_KEY_PREFIX + code, name.trim());
      localStorage.setItem(AVATAR_KEY_PREFIX + code, avatar);
      onJoined(data.player);
      toast.success("You're in the room.");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Couldn't join"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-sm mx-auto nk-card p-5 space-y-4" data-testid="player-join-form">
      {notOpen && (
        <div className="rounded-sm border border-nk-gold/40 bg-nk-gold/[0.06] p-3 text-center">
          <Lightning size={16} weight="duotone" className="mx-auto text-nk-gold mb-1" />
          <p className="text-xs text-nk-gold">The host hasn&apos;t opened the room yet — hang tight, you&apos;ll be able to join in a moment.</p>
        </div>
      )}
      <div>
        <label className="block text-xs text-nk-mute mb-1">Your name</label>
        <input
          data-testid="player-name-input" value={name} onChange={(e) => setName(e.target.value)}
          maxLength={24} placeholder="How you'll appear on the screen"
          className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
        />
      </div>
      <div>
        <label className="block text-xs text-nk-mute mb-2">Pick an avatar</label>
        <div className="grid grid-cols-6 gap-2" data-testid="player-avatar-grid">
          {AVATARS.map((a) => (
            <button key={a} type="button" onClick={() => setAvatar(a)}
              data-testid={`player-avatar-${a}`}
              className={`aspect-square rounded-sm border text-2xl flex items-center justify-center transition-all ${
                avatar === a ? "border-nk-gold bg-nk-gold/10 scale-105" : "border-nk-line hover:border-nk-gold/60"
              }`}
            >{a}</button>
          ))}
        </div>
      </div>
      <button type="submit" disabled={!canJoin || busy || notOpen}
        data-testid="player-join-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-nk-gold text-nk-bg font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <SignIn size={16} weight="bold" /> {busy ? "Joining…" : "Join event"}
      </button>
    </form>
  );
}


function QuestionCard({ code, q, myPick, setMyPick, myText, setMyText, lastResult, me, event }) {
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (q.revealed) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [q.revealed, q.qid]);

  const startedMs = q.started_at ? new Date(q.started_at).getTime() : now;
  const limit = (q.time_limit_s || 20) * 1000;
  const remaining = Math.max(0, limit - (now - startedMs));
  const remainingS = Math.ceil(remaining / 1000);
  const pct = Math.max(0, Math.min(100, (remaining / limit) * 100));
  const qtype = q.type || "multiple_choice";
  const isMC = qtype === "multiple_choice";
  const locked = isMC ? (myPick != null) : Boolean(myText && (q.__locked_text === myText));
  const timeUp = remaining <= 0 && !q.revealed;

  const isLadder = event?.mode === "ladder";
  const L = q.ladder || {};
  const isSolo = L.gameplay_flow === "solo";
  const iAmActive = isSolo && event?.ladder_active_contestant === me.player_tempid;
  const canAnswer = !isLadder || !isSolo || iAmActive;
  const eliminated = me.ladder_eliminated;
  const walked = me.ladder_walked_away;
  const isCash = L.reward_type === "cash";
  const symbol = L.currency_symbol || "$";
  const fmt = (a) => isCash ? `${symbol}${(a || 0).toLocaleString()}` : `${a} pts`;

  const pick = async (i) => {
    if (locked || busy || q.revealed || timeUp || !canAnswer || eliminated || walked) return;
    setBusy(true);
    setMyPick(i);
    try {
      await liveApi.post(`/live/events/${code}/answer`, {
        player_tempid: getPlayerTempId(),
        qid: q.qid,
        selected_index: i,
      });
    } catch (e2) {
      setMyPick(null);
      toast.error(e2.response?.data?.detail || "Couldn't submit");
    } finally { setBusy(false); }
  };

  const submitText = async () => {
    const text = (myText || "").trim();
    if (!text || locked || busy || q.revealed || timeUp || !canAnswer || eliminated || walked) return;
    setBusy(true);
    try {
      await liveApi.post(`/live/events/${code}/answer`, {
        player_tempid: getPlayerTempId(),
        qid: q.qid,
        answer_text: text,
      });
      q.__locked_text = text;   // mark locked so the input disables
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-sm mx-auto space-y-4" data-testid="player-question">
      <div className="nk-card p-4 flex items-center gap-3">
        <span className="text-2xl">{me.avatar}</span>
        <span className="flex-1 text-sm truncate">
          {me.display_name}
          {isLadder && eliminated && <span className="ml-2 text-[10px] text-nk-bad font-mono uppercase">out</span>}
          {isLadder && walked && <span className="ml-2 text-[10px] text-nk-mute font-mono uppercase">walked</span>}
          {iAmActive && <span className="ml-2 text-[10px] text-nk-gold font-mono uppercase">hot seat</span>}
        </span>
        <span className="font-mono text-sm text-nk-gold" data-testid="player-score">
          {isLadder ? fmt(me.score || 0) : `${me.score || 0} pts`}
        </span>
      </div>

      {isLadder && L.rung && (
        <div className="nk-card p-3 border border-nk-gold/40 bg-nk-gold/[0.06] flex items-center justify-between" data-testid="player-ladder-rung">
          <span className="font-mono text-[10px] uppercase text-nk-gold/70">Rung {L.rung_index + 1}</span>
          <span className="font-mono text-lg text-nk-gold">{fmt(L.rung.amount)}</span>
          {L.rung.safe && <span className="text-[10px] font-mono uppercase text-nk-gold">safe</span>}
        </div>
      )}

      <div className="nk-card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] uppercase text-nk-gold/70">
            {isLadder ? `Rung ${q.index}/${q.total}` : `Q${q.index}/${q.total}`} · {q.category || "—"}
          </p>
          <p className={`font-mono text-sm ${q.revealed ? "text-nk-gold" : remainingS <= 5 ? "text-nk-bad" : "text-nk-text"}`}>
            {q.revealed ? "REVEAL" : `${remainingS}s`}
          </p>
        </div>
        <div className="h-1 bg-nk-line rounded-full overflow-hidden mb-4">
          <div className={`h-full transition-all duration-200 ${
            q.revealed ? "bg-nk-gold" : remainingS <= 5 ? "bg-nk-bad" : "bg-nk-gold"
          }`} style={{ width: `${q.revealed ? 100 : pct}%` }} />
        </div>
        {!canAnswer && !eliminated && !walked && (
          <p className="mb-3 text-xs text-nk-mute text-center border border-nk-line/60 rounded-sm px-3 py-2">
            You&apos;re not in the hot seat this rung — watch the show.
          </p>
        )}
        {eliminated && (
          <p className="mb-3 text-xs text-nk-bad text-center border border-nk-bad/40 rounded-sm px-3 py-2">
            You&apos;re out of the ladder. Final score: {fmt(me.score || 0)}
          </p>
        )}
        {walked && (
          <p className="mb-3 text-xs text-nk-mute text-center border border-nk-line/60 rounded-sm px-3 py-2">
            You walked away with {fmt(me.score || 0)}.
          </p>
        )}
        {/* Emoji Phrase: enlarge the body font since it's just emojis */}
        {qtype === "emoji_phrase" ? (
          <div className="text-center mb-4 py-3">
            <p className="font-mono text-[10px] uppercase text-nk-gold/70 mb-2">Guess the phrase</p>
            <p className="text-5xl leading-tight" data-testid="player-emoji-body">{q.body}</p>
          </div>
        ) : (
          <p className="text-base font-medium mb-4" data-testid="player-question-body">{q.body}</p>
        )}

        {/* Literary Device: show the passage/snippet in a callout box */}
        {qtype === "literary_device" && q.snippet && (
          <blockquote className="mb-4 py-3 px-4 rounded-sm border-l-4 border-nk-gold bg-nk-gold/[0.06] italic text-sm text-nk-text/90"
                      data-testid="player-snippet">
            "{q.snippet}"
          </blockquote>
        )}

        {/* Unscramble: show the scrambled letters as a big monospace tile */}
        {qtype === "unscramble" && q.scrambled && (
          <div className="mb-4 py-4 px-3 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.06] text-center"
               data-testid="player-scrambled">
            <p className="font-mono text-[10px] uppercase text-nk-gold/70 mb-2">Scrambled</p>
            <p className="font-display text-3xl tracking-[0.4em] text-nk-gold">{q.scrambled}</p>
          </div>
        )}

        {/* Word Bank: chip-picker (tap one to lock in) */}
        {qtype === "word_bank" && (q.word_bank || []).length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2 justify-center" data-testid="player-word-bank">
            {(q.word_bank || []).map((w) => {
              const isMine = myText === w;
              const isCorrect = q.revealed && (q.accepted_answer || "").toLowerCase() === w.toLowerCase();
              const isWrong = q.revealed && isMine && !isCorrect;
              const disabled = locked || busy || q.revealed || timeUp || !canAnswer || eliminated || walked;
              return (
                <button
                  key={w}
                  onClick={() => { setMyText(w); setTimeout(() => submitText(), 0); }}
                  disabled={disabled}
                  data-testid={`player-word-chip-${w}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all disabled:cursor-not-allowed ${
                    isCorrect ? "border-nk-gold bg-nk-gold text-nk-bg" :
                    isWrong ? "border-nk-bad bg-nk-bad/15 text-nk-bad" :
                    isMine ? "border-nk-gold bg-nk-gold/15 text-nk-gold" :
                    disabled ? "border-nk-line opacity-40" :
                    "border-nk-line hover:border-nk-gold/60 text-nk-text"
                  }`}
                >{w}</button>
              );
            })}
          </div>
        )}

        {isMC ? (
        <ul className="space-y-2">
          {(q.options || []).map((o, i) => {
            const isMine = myPick === i;
            const isCorrect = q.revealed && q.answer_index === i;
            const isWrong = q.revealed && isMine && !isCorrect;
            const disabled = locked || busy || q.revealed || timeUp || !canAnswer || eliminated || walked;
            return (
              <li key={i}>
                <button
                  onClick={() => pick(i)} disabled={disabled}
                  data-testid={`player-option-${i}`}
                  className={`w-full text-left px-3 py-3 rounded-sm border text-sm flex items-center gap-3 transition-all disabled:cursor-not-allowed ${
                    isCorrect ? "border-nk-gold bg-nk-gold/15 text-nk-gold" :
                    isWrong ? "border-nk-bad bg-nk-bad/15 text-nk-bad" :
                    isMine ? "border-nk-gold bg-nk-gold/10 text-nk-gold" :
                    disabled ? "border-nk-line opacity-50" :
                    "border-nk-line hover:border-nk-gold/60"
                  }`}
                >
                  <span className="font-mono text-xs text-nk-mute w-4">{String.fromCharCode(65 + i)}.</span>
                  <span className="flex-1">{o}</span>
                  {isCorrect && <CheckCircle size={16} weight="fill" />}
                  {isWrong && <XCircle size={16} weight="fill" />}
                </button>
              </li>
            );
          })}
        </ul>
        ) : qtype === "word_bank" ? (
        /* Word bank chips already rendered above; only show correct-answer feedback on reveal */
        q.revealed && (
          <p className="text-xs text-nk-mute text-center" data-testid="player-accepted-answer">
            Correct answer: <span className="text-nk-gold font-mono">{q.accepted_answer}</span>
          </p>
        )
        ) : (
        /* Type-answer / Unscramble / Fill-blank — single text input + submit */
        <div className="space-y-3" data-testid="player-text-answer">
          <input
            type="text"
            value={myText}
            onChange={(e) => setMyText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitText(); }}
            disabled={locked || busy || q.revealed || timeUp || !canAnswer || eliminated || walked}
            placeholder={qtype === "fill_blank" ? "Fill in the blank…" : "Type your answer…"}
            data-testid="player-text-input"
            autoFocus
            className={`w-full px-4 py-3 rounded-sm border bg-nk-bg text-base focus:outline-none transition-colors font-medium ${
              q.revealed && lastResult?.is_correct ? "border-nk-gold text-nk-gold" :
              q.revealed && !lastResult?.is_correct ? "border-nk-bad text-nk-bad" :
              locked ? "border-nk-gold text-nk-gold" :
              "border-nk-line focus:border-nk-gold text-nk-text"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          />
          {q.revealed && (
            <p className="text-xs text-nk-mute text-center" data-testid="player-accepted-answer">
              Correct answer: <span className="text-nk-gold font-mono">{q.accepted_answer}</span>
            </p>
          )}
          {!locked && !q.revealed && (
            <button
              onClick={submitText}
              disabled={!myText.trim() || busy || timeUp || !canAnswer || eliminated || walked}
              data-testid="player-text-submit"
              className="w-full px-4 py-3 rounded-sm bg-nk-gold text-nk-bg font-medium text-sm hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Lock in answer
            </button>
          )}
        </div>
        )}
        {!q.revealed && locked && (
          <p className="text-xs text-nk-gold mt-3 text-center" data-testid="player-locked">
            Locked in — waiting for reveal…
          </p>
        )}
        {!q.revealed && !locked && timeUp && (
          <p className="text-xs text-nk-bad mt-3 text-center">Time up — waiting for reveal.</p>
        )}
        {q.revealed && lastResult && (
          <p className={`text-sm mt-3 text-center ${lastResult.is_correct ? "text-nk-gold" : "text-nk-bad"}`}
             data-testid="player-result">
            {lastResult.is_correct ? "✓ Correct" : "✗ Wrong"}
          </p>
        )}
      </div>
    </div>
  );
}


function EndedCard({ me, players }) {
  const rank = players.findIndex((p) => p.player_tempid === me.player_tempid) + 1;
  return (
    <div className="max-w-sm mx-auto space-y-4 text-center" data-testid="player-ended">
      <div className="nk-card p-6">
        <p className="eyebrow text-nk-gold">Event ended</p>
        <p className="mt-3 text-4xl">{me.avatar}</p>
        <p className="mt-2 text-lg font-medium">{me.display_name}</p>
        <p className="text-3xl font-mono text-nk-gold mt-3">{me.score || 0}</p>
        <p className="text-xs text-nk-mute mt-1">points · Rank #{rank || "—"}</p>
      </div>
      <p className="text-xs text-nk-mute">Thanks for playing!</p>
    </div>
  );
}


function PlayerLobby({ me, players, eventState }) {
  return (
    <div className="max-w-sm mx-auto space-y-4" data-testid="player-lobby">
      <div className="nk-card p-5 text-center">
        <div className="w-24 h-24 mx-auto rounded-full border-2 border-nk-gold bg-nk-charcoal flex items-center justify-center text-5xl mb-3">
          {me.avatar}
        </div>
        <p className="text-sm font-medium">{me.display_name}</p>
        <p className="text-[11px] text-nk-mute mt-1">
          {me.score > 0 ? `${me.score} pts` : "You're in the room"}
        </p>
      </div>
      <div className="nk-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 inline-flex items-center gap-2">
            <Users size={12} weight="fill" /> Playing with you
          </p>
          <p className="font-mono text-sm text-nk-gold">{players.length}</p>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {players.map((p) => (
            <div key={p.player_tempid} className="text-center" data-testid={`lobby-player-${p.player_tempid}`}>
              <div className={`w-12 h-12 mx-auto rounded-full border ${
                p.player_tempid === me.player_tempid ? "border-nk-gold" : "border-nk-line"
              } bg-nk-charcoal flex items-center justify-center text-2xl`}>
                {p.avatar}
              </div>
              <p className="mt-1 text-[10px] text-nk-mute truncate">{p.display_name}</p>
            </div>
          ))}
        </div>
      </div>
      {eventState === "lobby" && <p className="text-xs text-nk-mute text-center">Waiting for host to launch…</p>}
      {eventState === "running" && <p className="text-xs text-nk-gold text-center">The show is live — next prompt lands in a moment.</p>}
    </div>
  );
}
